import {
	CheckCircle2,
	Pause,
	Pencil,
	Play,
	PlayCircle,
	Plus,
	Trash2,
	UserCheck,
	Workflow,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchJson, sendJson } from "../../api/rest";
import {
	btn,
	ConfirmDialog,
	Empty,
	ErrorBanner,
	fmtNumber,
	fmtRelative,
	Panel,
	Spinner,
	StatusChip,
} from "../../components/flows/ui";
import type { FlowListItem, FlowStats, StartResult } from "../../types/flows";

const FILTERS = [
	{ id: "", label: "All" },
	{ id: "draft", label: "Draft" },
	{ id: "published", label: "Published" },
	{ id: "paused", label: "Paused" },
];

const Tile = ({
	icon: Icon,
	label,
	value,
	sub,
}: {
	icon: typeof Workflow;
	label: string;
	value: string;
	sub?: string;
}) => (
	<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
		<p className="flex items-center gap-2 font-medium text-[0.7rem] text-zinc-500 uppercase tracking-[0.14em]">
			<Icon className="h-3.5 w-3.5 text-zinc-400" />
			{label}
		</p>
		<p className="tnum mt-3 font-semibold text-[26px] text-ink leading-none">
			{value}
		</p>
		{sub && <p className="mt-2 text-[11px] text-zinc-400">{sub}</p>}
	</div>
);

const FlowsList = () => {
	const navigate = useNavigate();
	const [items, setItems] = useState<FlowListItem[]>([]);
	const [stats, setStats] = useState<FlowStats | null>(null);
	const [status, setStatus] = useState("");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState<number | null>(null);
	const [confirming, setConfirming] = useState<FlowListItem | null>(null);

	const load = useCallback(async () => {
		setError(null);
		try {
			const query = status ? `?status=${status}` : "";
			const [list, counts] = await Promise.all([
				fetchJson<{ items: FlowListItem[] }>(`/flows${query}`),
				fetchJson<FlowStats>("/flows/stats"),
			]);
			setItems(list.items);
			setStats(counts);
		} catch (e) {
			setError(String(e));
		} finally {
			setLoading(false);
		}
	}, [status]);

	useEffect(() => {
		load();
	}, [load]);

	const runNow = async (flow: FlowListItem) => {
		setBusy(flow.id);
		setError(null);
		try {
			const result = await sendJson<StartResult>(
				`/flows/${flow.id}/run`,
				"POST",
				{
					dryRun: flow.settings.dryRun,
				},
			);
			if (result.runId) navigate(`/flows/runs/${result.runId}`);
			else
				setError(`The run did not start: ${result.reason ?? "unknown reason"}`);
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(null);
		}
	};

	// Deleting is the server's decision, not the button's: a draft that has never run is removed,
	// and anything with history is archived instead. The dialog says which, so pressing it is not
	// a guess about whether the runs survive.
	const removeFlow = async (flow: FlowListItem) => {
		setBusy(flow.id);
		setError(null);
		try {
			await sendJson(`/flows/${flow.id}`, "DELETE");
			setConfirming(null);
			await load();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(null);
		}
	};

	const setFlowStatus = async (flow: FlowListItem, next: string) => {
		setBusy(flow.id);
		setError(null);
		try {
			await sendJson(`/flows/${flow.id}/status`, "POST", { status: next });
			await load();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(null);
		}
	};

	const archivesRatherThanDeletes = (flow: FlowListItem) =>
		flow.status !== "draft" || flow.runCount > 0;

	if (loading) return <Spinner />;

	return (
		<div className="space-y-6">
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
				<Tile
					icon={Workflow}
					label="Published flows"
					value={fmtNumber(stats?.published)}
					sub={`${fmtNumber(stats?.drafts)} drafts`}
				/>
				<Tile
					icon={PlayCircle}
					label="Runs, last 7 days"
					value={fmtNumber(stats?.runs7d)}
					sub={
						stats?.queue
							? `${stats.queue.active} running, ${stats.queue.queued} queued`
							: undefined
					}
				/>
				<Tile
					icon={CheckCircle2}
					label="Success rate"
					value={
						stats?.successRate7d === null ? "—" : `${stats?.successRate7d}%`
					}
					sub="of runs in the last 7 days"
				/>
				<Tile
					icon={UserCheck}
					label="Pending approvals"
					value={fmtNumber(stats?.pendingApprovals)}
					sub={`${fmtNumber(stats?.newCandidates)} new comparison candidates`}
				/>
			</div>

			<ErrorBanner error={error} />

			<Panel
				title="Flows"
				action={
					<div className="flex items-center gap-2">
						<div className="flex items-center gap-1 rounded-lg border border-slate-200 p-1">
							{FILTERS.map((f) => (
								<button
									key={f.id}
									type="button"
									onClick={() => setStatus(f.id)}
									className={`rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors ${
										status === f.id
											? "bg-primary text-white"
											: "text-slate-500 hover:bg-slate-100"
									}`}
								>
									{f.label}
								</button>
							))}
						</div>
						<Link to="/flows/templates" className={btn.primary}>
							<Plus className="h-4 w-4" /> New flow
						</Link>
					</div>
				}
			>
				{items.length === 0 ? (
					<Empty>
						No flows yet. Start from a{" "}
						<Link to="/flows/templates" className="font-medium text-primary">
							template
						</Link>{" "}
						— each one is a working journey you can edit.
					</Empty>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full text-left text-[13px]">
							<thead>
								<tr className="border-b border-slate-200 text-slate-500">
									<th className="pb-2 pr-3 font-medium">Name</th>
									<th className="pb-2 pr-3 font-medium">Status</th>
									<th className="pb-2 pr-3 font-medium">Last run</th>
									<th className="pb-2 pr-3 font-medium">Runs</th>
									<th className="pb-2 pr-3 font-medium">Sessions 7d</th>
									<th className="pb-2 pr-3 font-medium">Next fire</th>
									<th className="pb-2 font-medium">Actions</th>
								</tr>
							</thead>
							<tbody>
								{items.map((flow) => (
									<tr
										key={flow.id}
										className="border-b border-slate-100 last:border-0"
									>
										<td className="py-2.5 pr-3">
											<Link
												to={`/flows/${flow.id}`}
												className="font-medium text-slate-700 hover:text-primary"
											>
												{flow.name}
											</Link>
											{flow.description && (
												<p className="max-w-md truncate text-[11px] text-slate-400">
													{flow.description}
												</p>
											)}
										</td>
										<td className="py-2.5 pr-3">
											<StatusChip status={flow.status} />
											{flow.settings.dryRun && (
												<span className="ml-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">
													dry run
												</span>
											)}
										</td>
										<td className="py-2.5 pr-3">
											{flow.lastRunAt ? (
												<span className="flex items-center gap-1.5">
													<StatusChip status={flow.lastRunStatus} kind="run" />
													<span className="text-slate-500">
														{fmtRelative(flow.lastRunAt)}
													</span>
												</span>
											) : (
												<span className="text-slate-400">never</span>
											)}
										</td>
										<td className="py-2.5 pr-3 text-slate-600">
											{flow.runCount}
										</td>
										<td className="py-2.5 pr-3 text-slate-600">
											{flow.sessions7d}
										</td>
										<td className="py-2.5 pr-3 text-slate-500">
											{flow.nextRunAt ? (
												fmtRelative(flow.nextRunAt)
											) : flow.scheduleCount > 0 ? (
												<span className="text-slate-400">disabled</span>
											) : (
												<span className="text-slate-400">no schedule</span>
											)}
										</td>
										<td className="py-2.5">
											<div className="flex items-center gap-1">
												<button
													type="button"
													disabled={busy === flow.id}
													onClick={() => runNow(flow)}
													title="Run now"
													className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-primary disabled:opacity-40"
												>
													<Play className="h-4 w-4" />
												</button>
												{flow.status === "paused" ? (
													<button
														type="button"
														disabled={busy === flow.id}
														onClick={() => setFlowStatus(flow, "published")}
														title="Resume"
														className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-emerald-600 disabled:opacity-40"
													>
														<PlayCircle className="h-4 w-4" />
													</button>
												) : (
													<button
														type="button"
														disabled={
															busy === flow.id || flow.status !== "published"
														}
														onClick={() => setFlowStatus(flow, "paused")}
														title={
															flow.status === "published"
																? "Pause"
																: "Only a published flow can be paused"
														}
														className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-warning disabled:opacity-30"
													>
														<Pause className="h-4 w-4" />
													</button>
												)}
												<Link
													to={`/flows/${flow.id}`}
													title="Edit"
													className="rounded-md p-1.5 text-slate-500 hover:bg-canvas hover:text-primary"
												>
													<Pencil className="h-4 w-4" />
												</Link>
												<button
													type="button"
													disabled={busy === flow.id}
													onClick={() => setConfirming(flow)}
													title={
														archivesRatherThanDeletes(flow)
															? "Archive"
															: "Delete"
													}
													className="rounded-md p-1.5 text-slate-500 hover:bg-danger/5 hover:text-danger disabled:opacity-40"
												>
													<Trash2 className="h-4 w-4" />
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Panel>

			{confirming && (
				<ConfirmDialog
					title={
						archivesRatherThanDeletes(confirming)
							? `Archive "${confirming.name}"?`
							: `Delete "${confirming.name}"?`
					}
					body={
						archivesRatherThanDeletes(confirming) ? (
							<>
								It has run {fmtNumber(confirming.runCount)} time
								{confirming.runCount === 1 ? "" : "s"}, so it is archived rather
								than deleted — it leaves the list and stops running, and its
								runs, approvals and publications stay where they are.
							</>
						) : (
							<>
								A draft that has never run, so there is no history to keep. This
								removes it for good.
							</>
						)
					}
					confirmLabel={
						archivesRatherThanDeletes(confirming) ? "Archive" : "Delete"
					}
					danger
					busy={busy === confirming.id}
					onCancel={() => setConfirming(null)}
					onConfirm={() => removeFlow(confirming)}
				/>
			)}
		</div>
	);
};

export default FlowsList;
